'use client';

/**
 * Crowd Funding Plan - Custom Layout
 * All components are specific to this plan only, kept in this folder.
 */

import { PlanIntroSection } from '../common/plan-intro-section';
import { FeaturesSection } from '../common/features-section';
import { WhySoftwareSection } from '../common/why-software-section';
import { HowSoftwareWorksSection } from '../common/how-software-works-section';
import { WhatIsSoftwareSection } from '../common/what-is-software-section';
import { CommissionsSection } from '../common/commissions-section';
import { AdvantagesSection } from '../common/advantages-section';
import { WhatIsPlanSection } from './WhatIsPlanSection';
import { HowItWorksSection } from './HowItWorksSection';
import { UserPlus, TrendingUp, Award, Crown, Gift, Target } from 'lucide-react';

interface MLMPlan {
    slug: string;
    name: string;
    description: string;
    content: string;
    image?: string;
    features?: string[];
}

interface PlanDetailContentProps {
    plan: MLMPlan;
}

export default function PlanDetailContent({ plan }: PlanDetailContentProps) {
    return (
        <div className="min-h-screen bg-white dark:bg-gray-950">
            <PlanIntroSection
                plan={plan}
                translationNamespace="planDetails.crowdFundingPlan.hero"
                imageSrc="/images/mlm-plans/croudfunding_animate.gif"
                descriptionKeys={['description1', 'description2']}
            />
            <WhatIsPlanSection plan={plan} />
            <HowItWorksSection plan={plan} />
            <WhatIsSoftwareSection
                plan={plan}
                translationNamespace="planDetails.crowdFundingPlan.whatIsSoftware"
                imageSrc="/images/mlm-plans/mlm-software-demo.webp"
            />
            <HowSoftwareWorksSection plan={plan} translationNamespace="planDetails.crowdFundingPlan.howSoftwareWorks" />
            <FeaturesSection plan={plan} translationNamespace="planDetails.crowdFundingPlan.features" />
            <CommissionsSection
                plan={plan}
                translationNamespace="planDetails.crowdFundingPlan.commissions"
                commissions={[
                    { icon: UserPlus, color: 'primary', titleKey: 'sponsor.title', descriptionKey: 'sponsor.description' },
                    { icon: TrendingUp, color: 'blue', titleKey: 'level.title', descriptionKey: 'level.description' },
                    { icon: Award, color: 'green', titleKey: 'rankUpgrade.title', descriptionKey: 'rankUpgrade.description' },
                    { icon: Crown, color: 'purple', titleKey: 'royalty.title', descriptionKey: 'royalty.description' },
                    { icon: Target, color: 'orange', titleKey: 'bonanza.title', descriptionKey: 'bonanza.description' },
                    { icon: Gift, color: 'pink', titleKey: 'reward.title', descriptionKey: 'reward.description' },
                ]}
                gridCols="3"
            />
            <AdvantagesSection
                plan={plan}
                translationNamespace="planDetails.crowdFundingPlan.advantages"
                advantageKeys={['advantage1', 'advantage2', 'advantage3', 'advantage4', 'advantage5', 'advantage6', 'advantage7', 'advantage8']}
                gridCols="3"
            />
            <WhySoftwareSection plan={plan} translationNamespace="planDetails.crowdFundingPlan.whySoftware" />
        </div>
    );
}

